/**
 * SuperballBingoStyleStandard sections for GridEngine.
 * White card design with partner logo, jackpot display, and how-it-works grid.
 */
import React from 'react'
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Target from 'lucide-react/dist/esm/icons/target.js';
import Eye from 'lucide-react/dist/esm/icons/eye.js';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign.js';
import Circle from 'lucide-react/dist/esm/icons/circle.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import BingoBall from '../../../components/BingoBall'
import BingoBallTracker from '../../../components/BingoBallTracker'
import { useContent } from '../../../contexts/ContentContext'
import { formatCurrencyValue } from '../../../utils/formatCurrency'

/**
 * Hero section — white card, partner logo, Superball Bingo title, animated ball 76, stats grid.
 */
export function BingoStandardHeroSection({
  partner,
  mainDraw,
  editMode,
  handleTranslationUpdate,
  purchaseNavigate, language,
}) {
  const { c } = useContent()

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot || 5000)
  const sessionSales = mainDraw?.session_sales || 480
  const sessionPrize = Math.round(sessionSales * 0.25)
  const ticketsSold = mainDraw?.total_tickets_sold
  const drawnBalls = mainDraw?.drawn_balls || []
  const ballsRemaining = mainDraw?.balls_remaining ?? 63
  const superballDrawn = mainDraw?.superball_drawn || false

  return (
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-6">
        {partner?.partner_logo_url && (
          <div className="flex justify-center mb-4">
            <img
              src={partner.partner_logo_url}
              alt={partner.partner_name || 'Charity Logo'}
              className="h-16 md:h-20 object-contain"
            />
          </div>
        )}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white">
          <InlineTranslationEdit
            translationKey="superballBingo.title"
            value={c('superballBingo.title', 'Superball Bingo')}
            fallback="Superball Bingo"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h1>
        <div className="flex justify-center my-3">
          <BingoBall number={76} size="xl" animated />
        </div>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          <InlineTranslationEdit
            translationKey="superballBingo.subtitle"
            value={c('superballBingo.subtitle', '75 balls. One hides the golden Superball. Pick wisely and win big!')}
            fallback="75 balls. One hides the golden Superball. Pick wisely and win big!"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>

        <HeroPartnerGlassSelect className="mx-auto mt-6 w-full max-w-md" />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl p-4 border border-yellow-200 dark:border-yellow-700">
          <div className="flex items-center gap-2 text-xs font-semibold text-yellow-700 dark:text-yellow-400 uppercase tracking-wide mb-1">
            <Trophy className="w-4 h-4" />
            <InlineTranslationEdit
              translationKey="superballBingo.jackpotLabel"
              value={c('superballBingo.jackpotLabel', 'Progressive Jackpot')}
              fallback="Progressive Jackpot"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </div>
          <div className="text-2xl md:text-3xl font-black text-yellow-800 dark:text-yellow-300">
            {formatCurrencyValue(jackpotAmount, { language })}
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4 border border-green-200 dark:border-green-700">
          <div className="flex items-center gap-2 text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide mb-1">
            <DollarSign className="w-4 h-4" />
            <InlineTranslationEdit
              translationKey="superballBingo.sessionPrizeLabel"
              value={c('superballBingo.sessionPrizeLabel', 'Session Prize')}
              fallback="Session Prize"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </div>
          <div className="text-2xl md:text-3xl font-black text-green-800 dark:text-green-300">
            {formatCurrencyValue(sessionPrize, { language })}
          </div>
          <div className="text-xs text-green-600 dark:text-green-500">
            {c('SBStandard.sessionSalesPercent', '25% of session sales')}
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
          <div className="flex items-center gap-2 text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide mb-1">
            <Circle className="w-4 h-4" />
            <InlineTranslationEdit
              translationKey="superballBingo.ballsRemainingLabel"
              value={c('superballBingo.ballsRemainingLabel', 'Balls Remaining')}
              fallback="Balls Remaining"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </div>
          <div className="text-2xl md:text-3xl font-black text-blue-800 dark:text-blue-300">
            {ballsRemaining}
          </div>
          <div className="text-xs text-blue-600 dark:text-blue-500">
            {c('SBStandard.ofBalls', 'of 75 balls')}
          </div>
        </div>

        {ticketsSold != null && (
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-700">
            <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 dark:text-purple-400 uppercase tracking-wide mb-1">
              <Ticket className="w-4 h-4" />
              <InlineTranslationEdit
                translationKey="superballBingo.ticketsSoldLabel"
                value={c('superballBingo.ticketsSoldLabel', 'Tickets Sold')}
                fallback="Tickets Sold"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </div>
            <div className="text-2xl md:text-3xl font-black text-purple-800 dark:text-purple-300">
              {ticketsSold.toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * How-it-works section — 4-step grid (Buy Tickets, Pick Ball, Watch Draw, Win Prizes).
 */
export function BingoStandardHowItWorksSection({ editMode, handleTranslationUpdate }) {
  const { c } = useContent()

  const howItWorksSteps = [
    {
      icon: Ticket,
      title: 'Buy Tickets',
      description: 'Purchase your bingo tickets for the session',
      titleKey: 'SBStandard.step1Title',
      descKey: 'SBStandard.step1Desc',
    },
    {
      icon: Target,
      title: 'Pick Ball or Random',
      description: 'Choose your lucky ball or let us pick for you',
      titleKey: 'SBStandard.step2Title',
      descKey: 'SBStandard.step2Desc',
    },
    {
      icon: Eye,
      title: 'Watch Draw',
      description: 'Watch the live ball draw in real-time',
      titleKey: 'SBStandard.step3Title',
      descKey: 'SBStandard.step3Desc',
    },
    {
      icon: Gift,
      title: 'Win Prizes',
      description: 'Match the superball to win the jackpot!',
      titleKey: 'SBStandard.step4Title',
      descKey: 'SBStandard.step4Desc',
    },
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
        <InlineTranslationEdit
          translationKey="superballBingo.howItWorksTitle"
          value={c('superballBingo.howItWorksTitle', 'How It Works')}
          fallback="How It Works"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {howItWorksSteps.map((step, index) => (
          <div key={index} className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 bg-charity-primary/10 rounded-full flex items-center justify-center">
              <step.icon className="w-6 h-6 text-charity-primary" />
            </div>
            <div className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
              {c('SBStandard.stepLabel', 'Step')} {index + 1}
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
              <InlineTranslationEdit
                translationKey={step.titleKey}
                value={c(step.titleKey, step.title)}
                fallback={step.title}
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <InlineTranslationEdit
                translationKey={step.descKey}
                value={c(step.descKey, step.description)}
                fallback={step.description}
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
